import React, { useEffect, useState } from 'react';
import { BadgeCheck, Info, X } from 'lucide-react';
import type { UpdateUserAccountTypeRequest, UserAccountType } from '../services/api.types';
import { updateUserAccountType } from '../services/user.api';
import { useTranslations } from '../translations';
import AccountTypeForm, {
  AccountTypeFormErrors,
  emptyAccountTypeForm,
  validateAccountTypeForm,
} from './AccountTypeForm';
import './AccountTypeSelectionModal.css';

interface AccountTypeSelectionModalProps {
  onComplete: (accountType: UserAccountType) => void;
  onCancel: () => void;
}

const AccountTypeSelectionModal: React.FC<AccountTypeSelectionModalProps> = ({ onComplete, onCancel }) => {
  const t = useTranslations();
  const [form, setForm] = useState<UpdateUserAccountTypeRequest>(emptyAccountTypeForm());
  const [hasSelectedType, setHasSelectedType] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<AccountTypeFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleSubmit = async () => {
    if (!hasSelectedType || isSaving) return;
    const validationErrors = validateAccountTypeForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError(t.accountType.requiredFields);
      return;
    }
    setIsSaving(true);
    setFieldErrors({});
    setError('');
    try {
      await updateUserAccountType(form);
      onComplete(form.accountType);
    } catch (requestError) {
      console.error('Error updating account type:', requestError);
      setError(t.accountType.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="account-type-modal-overlay" onPointerDown={(event) => {
      if (event.target === event.currentTarget && !isSaving) onCancel();
    }}>
      <section
        className="account-type-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-type-modal-title"
        aria-describedby="account-type-modal-description"
      >
        <button
          type="button"
          className="account-type-modal-close"
          onClick={onCancel}
          disabled={isSaving}
          aria-label={t.common.close}
        >
          <X size={21} aria-hidden="true" />
        </button>

        <header className="account-type-modal-header">
          <span className="account-type-modal-header-icon" aria-hidden>
            <BadgeCheck size={22} />
          </span>
          <div>
            <p>{t.accountType.eyebrow}</p>
            <h2 id="account-type-modal-title">{t.accountType.title}</h2>
            <span id="account-type-modal-description">{t.accountType.description}</span>
          </div>
        </header>

        <div className="account-type-modal-body">
          <div className="account-type-modal-form">
            <AccountTypeForm
              value={form}
              onChange={(nextForm) => {
                setForm(nextForm);
                setFieldErrors({});
                setError('');
              }}
              selectedAccountType={hasSelectedType ? form.accountType : null}
              onTypeSelect={() => setHasSelectedType(true)}
              disabled={isSaving}
              errors={fieldErrors}
            />
          </div>

          <p className="account-type-modal-note">
            <Info size={17} aria-hidden />
            <span>{t.accountType.note}</span>
          </p>
          {error && <p className="account-type-modal-error" role="alert">{error}</p>}
        </div>

        <footer className="account-type-modal-actions">
          <button type="button" className="account-type-modal-cancel" onClick={onCancel} disabled={isSaving}>
            {t.accountType.cancel}
          </button>
          <button
            type="button"
            className="account-type-modal-submit"
            onClick={() => void handleSubmit()}
            disabled={!hasSelectedType || isSaving}
          >
            {isSaving ? t.accountType.saving : t.accountType.continue}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default AccountTypeSelectionModal;
